import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  ChartBarIcon,
  DocumentTextIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ListBulletIcon,
  ChartBarSquareIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ellianceService, { SENSOR_DATA_CACHE_MS, SENSOR_DATA_WINDOW_DAYS, CHART_MAX_POINTS } from '../services/ellianceService';
import { peekFarmerId, resolveFarmerId } from '../services/farmIdentity';
import { useFarm } from '../contexts/FarmContext';
import { useAuth } from '../contexts/AuthContext';
import { IOT_VENDORS } from '../config/iotVendors';
import FarmSelector from '../components/FarmSelector';
import OrganizationSelector from '../components/OrganizationSelector';
import PageErrorBoundary from '../components/PageErrorBoundary';

// ── Greenhouse sensor groups — matched to the Elliance staging dashboard ─────
// (https://staging.elliance-services.com/sulaiman_farm/): 7 groups, 8 sensor
// units. Both halves are now REAL:
//   • the registry (which sensors exist) comes from the middleware device-list
//   • the readings come from the middleware sensor-data endpoint, which went
//     live in Aug 2026 after being 404 for months
// `api` is the metric key the middleware publishes. `min`/`max` are physical
// and agronomic plausibility bounds — deliberately wide — used only to mark a
// reading as implausible, never to generate or clamp one. They are not target
// ranges or agronomic alerts: a pressure of 60 hPa is an instrumentation
// fault, whereas a pH of 6.6 is simply a reading.
const ELLIANCE_GROUPS = [
  {
    label: 'Water pH Sensors',
    sensors: [
      {
        key: 'wph1', name: 'Water pH Sensor 1', emoji: '💧',
        match: (s) => /^WPH-001/i.test(s.sensorId || ''),
        metrics: [{ key: 'wph1_ph', api: 'Water_pH', label: 'pH', unit: '', min: 5.5, max: 8.5, decimals: 1 }],
      },
      {
        key: 'wph2', name: 'Water pH Sensor 2', emoji: '💧',
        match: (s) => /^WPH-002/i.test(s.sensorId || ''),
        metrics: [{ key: 'wph2_ph', api: 'Water pH', label: 'pH', unit: '', min: 5.5, max: 8.5, decimals: 1 }],
      },
    ],
  },
  {
    label: 'Water Sensor (EC · TDS · Temp · Salinity)',
    sensors: [
      {
        key: 'water', name: 'Water Sensor', emoji: '🌊',
        match: (s) => /^WATER-/i.test(s.sensorId || ''),
        metrics: [
          { key: 'w_ec', api: 'Water EC', label: 'EC', unit: 'mS/cm', min: 0, max: 5, decimals: 2 },
          { key: 'w_tds', api: 'Water TDS', label: 'TDS', unit: 'ppm', min: 0, max: 2000, decimals: 1 },
          { key: 'w_temp', api: 'Water Temperature', label: 'Temperature', unit: '°C', min: 15, max: 35, decimals: 1 },
          { key: 'w_sal', api: 'Water Salinity', label: 'Salinity', unit: 'g/L', min: 0, max: 2, decimals: 2 },
        ],
      },
    ],
  },
  {
    label: 'Ambient Sensor (Temp · Humidity · Pressure)',
    sensors: [
      {
        key: 'ambient', name: 'Ambient Sensor', emoji: '⛅',
        match: (s) => /^AMB-/i.test(s.sensorId || ''),
        metrics: [
          { key: 'a_temp', api: 'Air Temperature', label: 'Air Temperature', unit: '°C', min: 15, max: 45, decimals: 1 },
          { key: 'a_hum', api: 'Air Humidity', label: 'Humidity', unit: '%', min: 20, max: 100, decimals: 1 },
          { key: 'a_press', api: 'Air Pressure', label: 'Pressure', unit: 'hPa', min: 950, max: 1050, decimals: 1 },
        ],
      },
    ],
  },
  {
    label: 'PAR Sensor',
    sensors: [
      {
        key: 'par', name: 'PAR Sensor', emoji: '☀️',
        match: (s) => /^PAR-/i.test(s.sensorId || ''),
        metrics: [{ key: 'par', api: 'PAR', label: 'PAR', unit: 'µmol/m²/s', min: 0, max: 2500, decimals: 1 }],
      },
    ],
  },
  {
    label: 'Soil pH Sensor',
    sensors: [
      {
        key: 'sph', name: 'Soil pH Sensor', emoji: '🧪',
        match: (s) => /^SPH-/i.test(s.sensorId || ''),
        metrics: [{ key: 's_ph', api: 'Soil pH', label: 'pH', unit: '', min: 4.5, max: 8.5, decimals: 1 }],
      },
    ],
  },
  {
    label: 'Soil Sensor (Temp · Moisture · EC · Salinity)',
    sensors: [
      {
        key: 'soil', name: 'Soil Sensor', emoji: '🌱',
        match: (s) => /^SOIL-/i.test(s.sensorId || ''),
        metrics: [
          { key: 's_temp', api: 'Soil Temperature', label: 'Temperature', unit: '°C', min: 15, max: 45, decimals: 1 },
          { key: 's_moist', api: 'Soil Moisture', label: 'Moisture', unit: '%', min: 0, max: 100, decimals: 1 },
          { key: 's_ec', api: 'Soil EC', label: 'EC', unit: 'mS/cm', min: 0, max: 5, decimals: 2 },
          { key: 's_sal', api: 'Soil Salinity', label: 'Salinity', unit: 'g/L', min: 0, max: 3, decimals: 2 },
        ],
      },
    ],
  },
  {
    label: 'Soil NPK Sensor',
    sensors: [
      {
        key: 'npk', name: 'Soil NPK Sensor', emoji: '📊',
        match: (s) => /^NPK-/i.test(s.sensorId || ''),
        metrics: [
          { key: 'n_n', api: 'Nitrogen', label: 'Nitrogen', unit: 'mg/kg', min: 0, max: 500, decimals: 1 },
          { key: 'n_p', api: 'Phosphorus', label: 'Phosphorus', unit: 'mg/kg', min: 0, max: 500, decimals: 1 },
          { key: 'n_k', api: 'Potassium', label: 'Potassium', unit: 'mg/kg', min: 0, max: 500, decimals: 1 },
        ],
      },
    ],
  },
];

// When the status service (API 2) fails or answers nothing, online / offline
// is derived from the selected farm's OWN readings instead: a registered
// sensor is online when at least one of its measurements has a reading newer
// than this, offline otherwise. The Elliance reference feed never counts.
const ONLINE_WINDOW_MINUTES = 15;

// The derived state of one registered device. Its measurements are the `api`
// keys of the registry sensor whose code prefix matches it — the same mapping
// the Greenhouse Monitor cards and the Sensor Data tab use — and
// `readings.latest` holds each metric's newest reading in the whole feed. A
// device the registry does not know has no measurements to look at, so it
// stays "unknown" rather than being counted offline.
const deriveStatus = (device, readings, now) => {
  const def = ELLIANCE_GROUPS.flatMap(g => g.sensors).find(d => d.match(device));
  if (!def) return { status: 'unknown', lastReadingAt: null };
  let lastReadingAt = null;
  for (const m of def.metrics) {
    const at = readings.latest?.[m.api]?.timestamp;
    if (at && (!lastReadingAt || at > lastReadingAt)) lastReadingAt = at;
  }
  const online = lastReadingAt && now.getTime() - lastReadingAt.getTime() <= ONLINE_WINDOW_MINUTES * 60 * 1000;
  return { status: online ? 'online' : 'offline', lastReadingAt };
};

// Elliance publishes readings under its own tenant while our farms wait to be
// onboarded. When the selected farm has no readings we fall back to this feed
// so the dashboard shows genuine middleware data — always labelled as such,
// never presented as this farm's own.
const ELLIANCE_REFERENCE_TENANT = { farmerId: '1', farmId: '1' };

// The sensor-data API ignores its date filters and returns the tenant's whole
// history (16.5 MB in Oct 2026), so the service keeps only the last
// SENSOR_DATA_WINDOW_DAYS days of points — there is nothing older to chart,
// which is why there is no monthly view.
const VIEW_WINDOW_HOURS = { daily: 24, weekly: 24 * SENSOR_DATA_WINDOW_DAYS };
const VIEW_WINDOW_LABEL = { daily: '24 hours', weekly: `${SENSOR_DATA_WINDOW_DAYS} days` };

const EMPTY_READINGS = { series: {}, latest: {}, latestAt: null, oldestAt: null, rows: 0 };

// True when the payload holds nothing older than the window the request asked
// for (startDate = now - SENSOR_DATA_WINDOW_DAYS). Today the middleware
// ignores startDate and sends the whole history, so this is false; once it
// honours the dates, what arrives is our own window, not the feed's full
// extent, and the page must not describe it as the feed's range.
const isWindowedPayload = (readings) => Boolean(
  readings.since && readings.oldestAt &&
  readings.oldestAt.getTime() >= readings.since.getTime() - 10 * 60 * 1000
);

// Readings that fall inside the window for the selected view, formatted for
// Recharts. Points are plotted where they actually occur rather than resampled
// onto a synthetic grid; a window holding more than CHART_MAX_POINTS readings
// (a week at 5-minute intervals is 2,016) is thinned evenly, always keeping
// the newest. `total` is the number of readings in the window before thinning.
const seriesForView = (points, view, now) => {
  if (!Array.isArray(points) || points.length === 0) return { points: [], total: 0 };
  const cutoff = now.getTime() - VIEW_WINDOW_HOURS[view] * 60 * 60 * 1000;
  // series are ascending, so the window is the tail
  let start = points.length;
  while (start > 0 && points[start - 1].timestamp.getTime() >= cutoff) start--;
  const inWindow = start === 0 ? points : points.slice(start);
  return {
    total: inWindow.length,
    points: ellianceService.downsample(inWindow, CHART_MAX_POINTS).map(p => ({
      // Elliance samples minutes apart, so a day-granularity label collides
      // for every point in a burst — always carry the time, and the date too
      // once the window spans more than a day.
      label: view === 'daily'
        ? p.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : `${p.timestamp.getDate()}/${p.timestamp.getMonth() + 1} `
          + p.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      value: p.value,
      at: p.timestamp,
    })),
  };
};

const formatAge = (date, now) => {
  if (!date) return 'never';
  const mins = Math.round((now.getTime() - date.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} h ago`;
  return `${Math.round(hrs / 24)} d ago`;
};

const pad2 = (n) => String(n).padStart(2, '0');

// "20:28" — or "20:28 on 30/9" when that moment was not today.
const formatClock = (date, now) => {
  const clock = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  return date.toDateString() === now.toDateString()
    ? clock
    : `${clock} on ${date.getDate()}/${date.getMonth() + 1}`;
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return null;
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1000 * 1000) return `${(bytes / 1000).toFixed(1)} kB`;
  return `${(bytes / (1000 * 1000)).toFixed(1)} MB`;
};

// Local wall-clock "YYYY-MM-DD HH:mm:00", the shape the datetime-local inputs
// on the Sensor Data tab read and write.
const toLocalRangeValue = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:00`;

// One sentence saying what went wrong with the latest sensor-data request,
// worded from what actually failed: "The sensor service is not responding
// (HTTP 502)." / "(timed out after 60 s)" / "(network error)".
const describeIssue = (issue) => {
  if (issue.kind === 'config') return issue.message;
  if (issue.kind === 'invalid') {
    return `The sensor service sent a response that could not be read${issue.detail ? ` (${issue.detail})` : ''}.`;
  }
  if (!issue.serviceDown) return `The sensor service rejected the request (${issue.reason}).`;
  return `The sensor service is not responding (${issue.reason}).`;
};

// Anything bigger than this is summarised before it goes into the API log
// (React state + localStorage) — a response body is never logged wholesale.
const LOG_BODY_LIMIT = 20000;
const compactForLog = (value) => {
  let json;
  try { json = JSON.stringify(value); } catch (e) { return { note: 'Not serialisable — omitted from the log' }; }
  if (json === undefined || json.length <= LOG_BODY_LIMIT) return value;
  return {
    note: 'Body too large for the log — summary only',
    items: Array.isArray(value) ? value.length : undefined,
    bytes: json.length,
  };
};

// One sensor card, staging-dashboard style: header (emoji, name, N parameters,
// registered badge, chevron), live metric tiles, expandable metric chart.
// Every value shown comes from the middleware; a metric with no published
// reading renders as "—" rather than being invented.
const EllianceSensorCard = ({ sensor, device, registryError, readings, now }) => {
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState('daily');
  const [metricKey, setMetricKey] = useState(sensor.metrics[0].key);
  const metric = sensor.metrics.find(m => m.key === metricKey) || sensor.metrics[0];
  const metricPoints = readings.series?.[metric.api];
  const chart = useMemo(
    () => (expanded ? seriesForView(metricPoints, view, now) : null),
    [expanded, metricPoints, view, now]
  );

  const reported = sensor.metrics.filter(m => readings.latest?.[m.api]).length;

  return (
    <div className="card p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <span className="text-xl">{sensor.emoji}</span>
          <div>
            <div className="text-sm font-semibold text-gray-900">{sensor.name}</div>
            <div className="text-xs text-gray-500">
              {reported}/{sensor.metrics.length} parameter{sensor.metrics.length > 1 ? 's' : ''} reporting
              {device && <span> · {device.sensorId}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {device ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">● Registered</span>
          ) : registryError ? (
            // the sensor list failed to load, so nobody knows whether this sensor exists
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                  title="The sensor list could not be loaded">Registration unknown</span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Not provisioned</span>
          )}
          <button
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 rounded-md bg-green-50 text-green-700 hover:bg-green-100"
            title={expanded ? 'Collapse chart' : 'Expand chart'}
          >
            <svg className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
      <div className="px-4 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        {sensor.metrics.map(m => {
          const reading = readings.latest?.[m.api];
          const outOfRange = reading && (reading.value < m.min || reading.value > m.max);
          // The tile shows the newest value in the whole feed for this metric,
          // however old — say so when it lags the rest of the feed.
          const lagging = reading && readings.latestAt
            && (readings.latestAt.getTime() - reading.timestamp.getTime()) > 60 * 60 * 1000;
          const windowed = isWindowedPayload(readings);
          return (
            <div key={m.key}>
              <div className="text-xs uppercase tracking-wide text-gray-500">{m.label}</div>
              {reading ? (
                <div className={`text-2xl font-bold ${outOfRange ? 'text-amber-600' : 'text-gray-900'}`}
                     title={outOfRange ? `Outside the expected ${m.min}–${m.max}${m.unit ? ' ' + m.unit : ''} range for this metric` : undefined}>
                  {reading.value.toFixed(m.decimals)}
                  {m.unit && <span className="ml-1 text-xs font-normal text-gray-500">{m.unit}</span>}
                  {outOfRange && <span className="ml-1 text-xs font-normal text-amber-600">⚠</span>}
                </div>
              ) : (
                <div className="text-2xl font-bold text-gray-300"
                     title={windowed
                       ? `No reading for this metric in the last ${SENSOR_DATA_WINDOW_DAYS} days`
                       : 'No reading published for this metric'}>—</div>
              )}
              {!reading && windowed && (
                <div className="text-[10px] text-gray-400">none in the last {SENSOR_DATA_WINDOW_DAYS} days</div>
              )}
              {lagging && (
                <div className="text-[10px] text-amber-600" title={`Newest reading for this metric: ${reading.timestamp.toLocaleString()}`}>
                  last published {reading.timestamp.toLocaleDateString()}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-xs text-gray-500">View:</span>
            {Object.keys(VIEW_WINDOW_HOURS).map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1 rounded-full text-xs font-medium ${view === v ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
            {sensor.metrics.length > 1 && (
              <span className="ml-auto flex flex-wrap gap-1">
                {sensor.metrics.map(m => (
                  <button key={m.key} onClick={() => setMetricKey(m.key)}
                    className={`px-2 py-0.5 rounded text-xs ${metric.key === m.key ? 'bg-green-100 text-green-800 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
                    {m.label}
                  </button>
                ))}
              </span>
            )}
          </div>
          {chart && chart.points.length > 0 ? (
            <>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chart.points} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                {/* domain follows the published readings — the vendor's values
                    routinely fall outside the metric's plausible range, and
                    clamping them would hide that */}
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [`${v} ${metric.unit}`.trim(), metric.label]}
                         labelFormatter={(l, p) => p?.[0]?.payload?.at?.toLocaleString() || l} />
                {/* dots only while they are still distinguishable */}
                <Line type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2}
                      dot={chart.points.length > 100 ? false : { r: 2 }} name={metric.label} />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-1 text-[11px] text-gray-400">
              {chart.total > chart.points.length
                ? `Showing ${chart.points.length} of ${chart.total.toLocaleString()} readings from the last ${VIEW_WINDOW_LABEL[view]}, evenly sampled — the newest is always included.`
                : `${chart.total.toLocaleString()} reading${chart.total > 1 ? 's' : ''} from the last ${VIEW_WINDOW_LABEL[view]}.`}
            </div>
            </>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-gray-400">
              No {metric.label} readings published in the last {VIEW_WINDOW_LABEL[view]}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// The Greenhouse Monitor view — 7 sensor groups matching the Elliance staging
// dashboard (sulaiman_farm). Registry and readings both come from the Kapitani
// middleware; `source` records which tenant the readings were served for.
// `meta` describes the load behind the readings on screen ({ loadedAt,
// summary }); `issue` is set when the latest request failed, in which case the
// readings are the last good ones. `registryError` is set when the sensor
// list could not be loaded, so the registry is unknown rather than empty.
const GreenhouseMonitor = ({ registry, registryError, readings, source, loading, issue, meta, farmLabel, canPickFarm, onRefresh }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const stale = readings.latestAt && (now.getTime() - readings.latestAt.getTime()) > 60 * 60 * 1000;
  const hasReadings = readings.rows > 0;
  const size = formatBytes(meta?.summary?.bytes);
  const windowed = isWindowedPayload(readings);

  return (
    <div className="space-y-6">
      {/* Latest request failed — amber while the last good data is still on
          screen, red when there is nothing to show at all */}
      {issue && hasReadings && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3" role="status">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>{describeIssue(issue)}</strong>{' '}
            {meta?.loadedAt ? `Showing data loaded at ${formatClock(meta.loadedAt, now)}.` : 'Showing the last data loaded.'}
          </div>
        </div>
      )}
      {issue && !hasReadings && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" role="alert">
          <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">
            <strong>Could not load sensor data.</strong> {describeIssue(issue)} No earlier data is
            held in this browser session.
          </div>
        </div>
      )}

      {/* Data-source banner — states exactly whose readings these are */}
      {source === 'reference' ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Elliance reference feed — not this farm.</strong> These are live readings from the
            Kapitani middleware, but published under Elliance's own tenant
            (farmerId {ELLIANCE_REFERENCE_TENANT.farmerId}/farmId {ELLIANCE_REFERENCE_TENANT.farmId}).
            No readings have been published for {farmLabel} yet
            {registryError ? '.' : ` — its ${registry.length} sensors are registered but silent.`}{' '}
            The dashboard switches to this farm's own data automatically once
            Elliance starts publishing for it.
          </div>
        </div>
      ) : source === 'all-farms' ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <strong>{canPickFarm ? 'Choose a farm to see its readings.' : 'No farm is selected.'}</strong>{' '}
            Sensor readings are published farm by farm, so none are loaded
            {canPickFarm ? ' while All farms is selected. Pick a farm in the farm selector at the top of this page.' : '.'}
          </div>
        </div>
      ) : source === 'farm' ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-900">
            <strong>Live readings for {farmLabel}</strong> — served by the Kapitani middleware
            sensor-data API.
          </div>
        </div>
      ) : !loading && !issue && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
          <InformationCircleIcon className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <strong>No readings available.</strong> The middleware holds no sensor data for {farmLabel},
            and the Elliance reference feed is empty too.
          </div>
        </div>
      )}

      {/* What the feed actually contains — its span, its newest reading and
          how fresh the copy on screen is */}
      {hasReadings && readings.latestAt && readings.oldestAt && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-xs text-gray-600 space-y-1">
          <div>
            <strong className="text-gray-800">{windowed ? 'Readings received:' : 'Feed range:'}</strong>{' '}
            {readings.oldestAt.toLocaleString()} → {readings.latestAt.toLocaleString()}
            {' '}· {readings.rows.toLocaleString()} rows{size ? ` · ${size}` : ''}
            {windowed && ` · only the last ${SENSOR_DATA_WINDOW_DAYS} days are requested, so the feed may hold older readings`}
          </div>
          <div>
            <strong className="text-gray-800">Newest reading:</strong>{' '}
            {readings.latestAt.toLocaleString()} ({formatAge(readings.latestAt, now)})
            {meta?.summary?.newest && (
              <span> — published as {meta.summary.newest} (the feed gives no time zone; read as UTC), shown here in local time</span>
            )}
          </div>
          {meta?.loadedAt && (
            <div>
              <strong className="text-gray-800">Loaded:</strong>{' '}
              {formatClock(meta.loadedAt, now)}
              {Number.isFinite(meta.summary?.durationMs) && ` in ${(meta.summary.durationMs / 1000).toFixed(1)} s`}
              {' '}· refreshed every {Math.round(SENSOR_DATA_CACHE_MS / 60000)} minutes while this tab is visible
              {' '}· tiles show each metric's newest reading; charts and tables keep the last {SENSOR_DATA_WINDOW_DAYS} days
            </div>
          )}
        </div>
      )}

      {/* not while the service is failing — then nobody knows what Elliance has pushed */}
      {stale && !issue && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-3">
          <ClockIcon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-orange-900">
            Feed is stale — the most recent reading is from{' '}
            <strong>{readings.latestAt.toLocaleString()}</strong> ({formatAge(readings.latestAt, now)}).
            Elliance has not pushed anything since.
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-400">{farmLabel}</div>
        <div className="flex items-center gap-2">
          <button onClick={onRefresh} disabled={loading}
            title="Download the feed again now, bypassing the 5-minute cache"
            className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            Refresh
          </button>
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium ${
            loading ? 'bg-gray-50 border-gray-200 text-gray-600'
              : readings.latestAt ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
            <span className={`h-2 w-2 rounded-full ${loading ? 'bg-gray-400 animate-pulse' : readings.latestAt ? 'bg-green-500' : 'bg-gray-400'}`} />
            {loading ? 'Loading…'
              : readings.latestAt ? `Last reading ${formatAge(readings.latestAt, now)}`
              : 'No readings'}
          </div>
        </div>
      </div>

      {ELLIANCE_GROUPS.map(group => (
        <div key={group.label}>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-green-700">{group.label}</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800 border border-green-200">
              {group.sensors.length} unit{group.sensors.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className={group.sensors.length > 1 ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : ''}>
            {group.sensors.map(sensor => (
              <EllianceSensorCard
                key={sensor.key}
                sensor={sensor}
                device={registry.find(s => sensor.match(s))}
                registryError={registryError}
                readings={readings}
                now={now}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};


const EllianceContent = () => {
  const { user } = useAuth();
  const ellianceVendorId = IOT_VENDORS.ELLIANCE.vendorId;
  const { farms: contextFarms, loading: farmsLoading, selectedFarmId, isAdmin, hasOrganizations, hasFarms } = useFarm();

  const farms = contextFarms?.map(f => ({
    id: f.farm_id,
    name: `${f.farm_name || 'Unnamed Farm'}${f.farmsize ? ` (${f.farmsize} hectares)` : ''}`,
    farmName: f.farm_name,
  })) || [];

  const [sensors, setSensors] = useState([]);
  const [sensorsError, setSensorsError] = useState(null); // why the sensor list could not be loaded, if it could not
  const [sensorStatus, setSensorStatus] = useState([]);
  const [statusState, setStatusState] = useState('loading'); // 'loading' | 'ok' | 'unavailable'
  const [selectedTab, setSelectedTab] = useState('monitor');
  const [isLoading, setIsLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [apiLogs, setApiLogs] = useState([]);
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [sensorData, setSensorData] = useState(null);
  const [sensorDataSource, setSensorDataSource] = useState(null); // 'farm' | 'reference'
  const [sensorDataNote, setSensorDataNote] = useState(null); // { loadedAt, since, clipped, issue } for the table on screen
  const [sensorDataLoading, setSensorDataLoading] = useState(false);
  // Local wall-clock, as the datetime-local inputs show it.
  const [dateRange, setDateRange] = useState(() => ({
    startDate: toLocalRangeValue(new Date(Date.now() - SENSOR_DATA_WINDOW_DAYS * 24 * 60 * 60 * 1000)),
    endDate: toLocalRangeValue(new Date())
  }));

  // Greenhouse Monitor readings from the sensor-data API — already normalised
  // and trimmed by the service. The raw payload never reaches React state.
  const [readings, setReadings] = useState(EMPTY_READINGS);
  const [readingsSource, setReadingsSource] = useState(null); // 'farm' | 'reference' | 'all-farms' | null
  const [readingsMeta, setReadingsMeta] = useState(null); // { loadedAt: Date, summary } for the readings on screen
  const [readingsIssue, setReadingsIssue] = useState(null); // the latest request's failure, if it failed
  const [readingsLoading, setReadingsLoading] = useState(true);
  const refreshReadingsRef = useRef(null); // manual Refresh -> the poll effect's loader
  const shownReadingsRef = useRef({ source: null, farmKey: null }); // whose readings are on screen
  const sensorsRequestRef = useRef(0); // number of the latest fetchSensors() call - only that one may write to the page
  const sensorsListKeyRef = useRef(null); // "farmerId|farmId" of the sensor list on screen, null when none is held
  const selectedSensorRef = useRef(null); // id of the sensor picked right now, for a Sensor Data reply that arrives late
  selectedSensorRef.current = selectedSensor?.id;

  // Load logs from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('elliance_api_logs');
    if (saved) {
      try { setApiLogs(JSON.parse(saved)); } catch (e) {}
    }
  }, []);

  // The log lives in React state and localStorage, so it only ever holds
  // summaries: sensor-data entries carry row count / size / newest timestamp /
  // duration, and compactForLog() stops any other body from growing past
  // LOG_BODY_LIMIT.
  const addLog = (operation, request, response, status) => {
    const entry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      operation,
      request,
      response: compactForLog(response),
      status
    };
    setApiLogs(prev => {
      const newLogs = [entry, ...prev].slice(0, 100);
      try { localStorage.setItem('elliance_api_logs', JSON.stringify(newLogs)); } catch (e) {}
      return newLogs;
    });
  };

  // Fetch sensors from middleware
  const fetchSensors = async (farmIdToFetch) => {
    const farmId = (!farmIdToFetch || farmIdToFetch === 'all') ? 'ALL' : farmIdToFetch;
    // A late reply from an earlier farm or an earlier Refresh click must not
    // replace what the latest call loaded: only the latest call writes state.
    const requestId = ++sensorsRequestRef.current;
    const isLatest = () => sensorsRequestRef.current === requestId;
    setIsLoading(true);
    setStatusState('loading');
    // A farm's sensors are listed under the farmer id of its middleware farm
    // record (see farmIdentity); without one, and for all farms, under the
    // logged-in user's id
    const farmerId = await resolveFarmerId(farmId, user?.id || '');
    if (!isLatest()) return;
    // The list did not load. When the list on screen is this same farm's (a
    // Refresh that failed), it stays and the failure is reported next to it;
    // otherwise there is no list to show and the page says why.
    const listKey = `${farmerId}|${farmId}`;
    const listFailed = (reason) => {
      if (sensorsListKeyRef.current === listKey) {
        setFeedback({ type: 'error', message: `Could not refresh the sensor list (${reason}). The list loaded earlier is still shown.` });
        setTimeout(() => setFeedback(null), 5000);
        return;
      }
      sensorsListKeyRef.current = null;
      setSensors([]);
      setSensorsError(reason);
    };

    try {
      const result = await ellianceService.listSensors(farmId, farmerId);
      addLog('LIST_SENSORS', { farmId, farmerId, vendorId: ellianceVendorId }, result.data || result.message, result.success ? 'success' : 'error');
      if (!isLatest()) return;

      // A failed request - or a reply that is not a list - is not "this farm
      // has no sensors": say so on the page instead of showing an empty list.
      if (result.success && Array.isArray(result.data)) {
        setSensors(result.data);
        setSensorsError(null);
        sensorsListKeyRef.current = listKey;
      } else {
        listFailed(result.success
          ? 'unreadable response'
          : (result.error?.reason || result.message || 'unknown error'));
      }
    } catch (error) {
      addLog('LIST_SENSORS', {}, { error: error.message }, 'error');
      if (!isLatest()) return;
      listFailed(error.message || 'unexpected error');
    } finally {
      if (isLatest()) setIsLoading(false);
    }

    // Also fetch status. The device-status route is 404 or hangs, so it is
    // asked after the list is already on screen and gives up after 20 s. While
    // it runs every sensor stays "unknown"; once it has failed, or answered
    // nothing, the page derives online / offline from the farm's own readings
    // instead (see deriveStatus and statusSource below).
    try {
      const statusResult = await ellianceService.getSensorStatus(farmId, farmerId);
      addLog('SENSOR_STATUS', { farmId, farmerId, vendorId: ellianceVendorId }, statusResult.data || statusResult.message, statusResult.success ? 'success' : 'error');
      if (!isLatest()) return;

      // No usable answer: fall back to the readings - never "0 online, 0 offline".
      if (statusResult.success && Array.isArray(statusResult.data)) {
        setSensorStatus(statusResult.data);
        setStatusState('ok');
      } else {
        setSensorStatus([]);
        setStatusState('unavailable');
      }
    } catch (error) {
      addLog('SENSOR_STATUS', {}, { error: error.message }, 'error');
      if (!isLatest()) return;
      setSensorStatus([]);
      setStatusState('unavailable');
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchSensors(selectedFarmId);
    }
  }, [user?.id, selectedFarmId]);

  // Resolve the readings for a farm — the one path both the Greenhouse Monitor
  // and the Sensor Data tab use. Elliance has not yet onboarded our farms into
  // its publisher, so when the farm's own feed is empty we fall back to
  // Elliance's reference tenant — real middleware data, clearly labelled in
  // the UI as belonging to a different farm.
  //
  // Every request goes through ellianceService.getReadings(), which caches a
  // tenant's result for 5 minutes and shares a request already in flight, so
  // opening the page costs one download of the big reference feed no matter
  // how many callers ask. `force` bypasses the cache (manual Refresh and the
  // poll). `quiet` keeps the background poll's successes out of the API log;
  // cache hits and joined requests are never logged, because this call made
  // no request of its own.
  //
  // Resolves to { source, readings, summary, loadedAt, issue }. When a request
  // fails, `issue` describes the failure and the readings are the last good
  // ones held for this farm (or its reference fallback), if any.
  const resolveReadings = useCallback(async (farmIdToFetch, { force = false, quiet = false } = {}) => {
    const farmId = (!farmIdToFetch || farmIdToFetch === 'all') ? '' : String(farmIdToFetch);
    // All farms: readings are published per farm and there is no farm to ask
    // for. Going straight to the reference feed here would keep showing it
    // even after a farm's own readings exist, so nothing is requested and the
    // monitor asks the user to choose a farm instead.
    if (!farmId) {
      return { source: 'all-farms', readings: EMPTY_READINGS, summary: null, loadedAt: null, issue: null };
    }
    const attempts = [
      // The farm's readings are asked for under the farmer id of its
      // middleware farm record (see farmIdentity), else the user's id
      { source: 'farm', farmerId: await resolveFarmerId(farmId, user?.id || ''), farmId },
      { source: 'reference', ...ELLIANCE_REFERENCE_TENANT },
    ];

    let issue = null;
    for (const attempt of attempts) {
      const result = await ellianceService.getReadings({
        farmerId: attempt.farmerId,
        farmId: attempt.farmId,
        force,
      });
      if (!result.fromCache && !result.shared && (!quiet || !result.success)) {
        addLog('SENSOR_DATA', { source: attempt.source, ...result.request },
          result.success
            ? {
                rows: result.summary.rows,
                bytes: result.summary.bytes,
                newest: result.summary.newest,
                durationMs: result.summary.durationMs,
              }
            : { error: result.message, durationMs: result.durationMs },
          result.success ? 'success' : 'error');
      }

      if (result.success) {
        if (result.readings.rows > 0) {
          return { source: attempt.source, readings: result.readings, summary: result.summary, loadedAt: result.loadedAt, issue: null };
        }
        continue;
      }

      issue = result;
      // 5xx, timeout or network error: the service itself is struggling, so
      // do not follow up with a second (16 MB) request.
      if (result.serviceDown) break;
    }

    if (issue) {
      for (const attempt of attempts) {
        const good = ellianceService.peekReadings(attempt);
        if (good && good.readings.rows > 0) {
          return { source: attempt.source, readings: good.readings, summary: good.summary, loadedAt: good.loadedAt, issue };
        }
      }
    }
    return { source: null, readings: EMPTY_READINGS, summary: null, loadedAt: null, issue };
  }, [user?.id]);

  // Load the Greenhouse Monitor readings and keep them fresh.
  //   - The feed publishes every 5 minutes, so that is the poll interval; the
  //     middleware has no streaming or webhook option.
  //   - The next poll is due one interval after the data on screen was loaded
  //     (after a failure: one interval after the failure — never sooner).
  //   - No timer runs while the browser tab is hidden. When it becomes visible
  //     again the countdown resumes, which fires at once if the data is
  //     already older than the interval.
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    let running = false;
    let timer = null;
    let nextDueAt = 0;

    const schedule = (dueAt) => {
      nextDueAt = dueAt;
      clearTimeout(timer);
      timer = null;
      if (document.visibilityState === 'hidden') return; // resumed by onVisibility
      timer = setTimeout(() => load({ force: true, quiet: true }), Math.max(dueAt - Date.now(), 0));
    };

    const load = async (options) => {
      if (running) return;
      running = true;
      clearTimeout(timer);
      timer = null;
      setReadingsLoading(true);
      // resolveReadings() does not reject today. The try/finally is there so
      // that if it ever does, the page still leaves "Loading…", Refresh works
      // again and the poll is rescheduled.
      let outcome = null;
      let failure = null;
      try {
        outcome = await resolveReadings(selectedFarmId, options);
      } catch (error) {
        console.error('❌ [Elliance] Loading sensor data failed:', error);
        failure = {
          kind: 'config',
          message: `Sensor data could not be processed (${error?.message || 'unexpected error'}).`,
          serviceDown: false,
          failedAt: new Date(),
        };
      } finally {
        running = false;
      }
      if (cancelled) return;

      if (outcome) {
        setReadings(outcome.readings);
        setReadingsSource(outcome.source);
        setReadingsMeta(outcome.loadedAt ? { loadedAt: outcome.loadedAt, summary: outcome.summary } : null);
        setReadingsIssue(outcome.issue);
        shownReadingsRef.current = { source: outcome.source, farmKey: String(selectedFarmId) };
      } else {
        // keep whatever is on screen and say what went wrong
        setReadingsIssue(failure);
      }
      setReadingsLoading(false);

      // Next poll: one interval after the data on screen was loaded - or,
      // after a failure, one interval after the failure itself (a failure
      // served from the cache keeps its original time, so a remount does not
      // push the retry further out).
      const issue = outcome ? outcome.issue : failure;
      const failedAt = issue && issue.failedAt instanceof Date ? issue.failedAt.getTime() : Date.now();
      const basis = issue || !outcome.loadedAt ? failedAt : outcome.loadedAt.getTime();
      schedule(basis + SENSOR_DATA_CACHE_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(timer);
        timer = null;
      } else if (!running && nextDueAt) {
        schedule(nextDueAt);
      }
    };

    // Readings that belong to another farm must not stay on screen under
    // this farm's name while its own request runs (up to 60 s). Reference
    // readings are farm-independent and labelled as such, so they stay. The
    // All farms "choose a farm" prompt goes too once a farm has been chosen.
    const shown = shownReadingsRef.current;
    if ((shown.source === 'farm' || shown.source === 'all-farms') && shown.farmKey !== String(selectedFarmId)) {
      const own = selectedFarmId && selectedFarmId !== 'all'
        ? ellianceService.peekReadings({ farmerId: peekFarmerId(String(selectedFarmId), user.id), farmId: String(selectedFarmId) })
        : null;
      const seed = own && own.readings.rows > 0 ? own : null;
      setReadings(seed ? seed.readings : EMPTY_READINGS);
      setReadingsSource(seed ? 'farm' : null);
      setReadingsMeta(seed ? { loadedAt: seed.loadedAt, summary: seed.summary } : null);
      setReadingsIssue(null);
      shownReadingsRef.current = { source: seed ? 'farm' : null, farmKey: String(selectedFarmId) };
    }

    refreshReadingsRef.current = () => load({ force: true });
    document.addEventListener('visibilitychange', onVisibility);
    load();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      refreshReadingsRef.current = null;
    };
  }, [resolveReadings, selectedFarmId, user?.id]);

  // Where the online / offline state on the page comes from:
  //   loading      - the status call (API 2), or the farm's first readings
  //                  load, is still running
  //   api          - API 2 answered with real data: preferred whenever it does
  //   derived      - API 2 failed or said nothing, so the state is derived from
  //                  this farm's own readings (never the reference feed). Only
  //                  a cleanly loaded feed qualifies: after a failed poll the
  //                  last good snapshot stays on the monitor, but it only ages,
  //                  so judging it against the clock would show every sensor
  //                  offline for a middleware outage.
  //   no-readings  - API 2 failed or said nothing and the farm has no readings
  //                  to derive from (its last request succeeded but was empty)
  //   unavailable  - nothing to go on: All farms is selected, or the latest
  //                  readings request failed
  const apiAnswered = statusState === 'ok'
    && (sensors.length === 0 || sensors.some(s => ['online', 'offline'].includes(sensorStatus.find(st => st.id === s.id)?.status)));
  const statusSource =
    statusState === 'loading' ? 'loading'
    : apiAnswered ? 'api'
    : readingsSource === 'farm' && !readingsIssue ? 'derived'
    : readingsLoading && !readingsSource ? 'loading'
    : !readingsIssue && (readingsSource === 'reference' || !readingsSource) ? 'no-readings'
    : 'unavailable';
  // The derived state is relative to the moment of rendering; the page
  // re-renders at least on every 5-minute readings poll.
  const now = new Date();

  // Merge sensor list with status
  const mergedSensors = sensors.map(s => (
    statusSource === 'derived'
      ? { ...s, ...deriveStatus(s, readings, now) }
      : { ...s, status: sensorStatus.find(st => st.id === s.id)?.status || 'unknown' }
  ));

  // Registered devices whose code prefix the registry does not know have no
  // measurements to derive from: they stay "unknown", are counted in neither
  // card, and the Sensor Status tab says so, so Online + Offline + these = Total.
  const unmatchedCount = statusSource === 'derived' ? mergedSensors.filter(s => s.status === 'unknown').length : 0;

  const filteredSensors = selectedFarmId === 'all' ? mergedSensors : mergedSensors.filter(s => String(s.farmId) === String(selectedFarmId));

  // Fetch sensor data — from the same cached feed as the Greenhouse Monitor,
  // so this does not download anything unless the cache has expired.
  const fetchSensorData = async (sensor) => {
    setSensorDataLoading(true);
    setSensorData(null);
    const farmId = sensor.farmId || selectedFarmId || '';

    try {
      // Same farm-then-reference resolution as the monitor.
      const outcome = await resolveReadings(farmId);
      // Another sensor was picked while this was loading: these readings are
      // not its own, so they are not shown under its name.
      if (selectedSensorRef.current !== sensor.id) return;

      // The middleware accepts startDate/endDate but ignores them, so the range
      // is applied here — to the last SENSOR_DATA_WINDOW_DAYS days of readings,
      // which is all the service keeps.
      const from = new Date(dateRange.startDate.replace(' ', 'T'));
      const to = new Date(dateRange.endDate.replace(' ', 'T'));
      // The middleware ignores the sensor id too, so the picked sensor is
      // applied here as well: keep only the metrics that belong to it, using
      // the same sensor -> metrics mapping as the Greenhouse Monitor cards.
      // A sensor the mapping does not know cannot be narrowed (ownMetrics null).
      const sensorDef = ELLIANCE_GROUPS.flatMap(g => g.sensors).find(d => d.match(sensor));
      const ownMetrics = sensorDef ? sensorDef.metrics.map(m => m.api) : null;
      const windowed = {};
      for (const [metric, points] of Object.entries(outcome.readings.series)) {
        if (ownMetrics && !ownMetrics.includes(metric)) continue;
        const kept = points.filter(pt => pt.timestamp >= from && pt.timestamp <= to);
        if (kept.length) windowed[metric] = kept;
      }
      const hasData = Object.keys(windowed).length > 0;
      const since = outcome.readings.since || null;

      if (outcome.issue && !hasData) {
        setFeedback({ type: 'error', message: describeIssue(outcome.issue) });
        setTimeout(() => setFeedback(null), 5000);
      }
      setSensorData(windowed);
      setSensorDataSource(hasData ? outcome.source : null);
      setSensorDataNote({
        loadedAt: outcome.loadedAt,
        since,
        // the default range starts a few minutes before the kept window does,
        // so only call it clipped when it reaches back meaningfully further
        clipped: Boolean(since) && (since.getTime() - from.getTime()) > 60 * 60 * 1000,
        issue: outcome.issue,
        // false when the request failed and no earlier data is held either
        held: outcome.readings.rows > 0,
        // true when only the picked sensor's own metrics were kept
        narrowed: Boolean(ownMetrics),
        // true when nothing was requested: All farms is selected and the
        // sensor record names no farm of its own
        noFarm: outcome.source === 'all-farms',
      });
    } catch (error) {
      // resolveReadings() does not reject today; never leave the button on "Loading..."
      console.error('❌ [Elliance] Fetching sensor data failed:', error);
      if (selectedSensorRef.current !== sensor.id) return;
      setSensorData({});
      setSensorDataSource(null);
      setSensorDataNote({
        loadedAt: null,
        since: null,
        clipped: false,
        issue: { kind: 'config', message: `Sensor data could not be processed (${error?.message || 'unexpected error'}).` },
        held: false,
      });
    } finally {
      setSensorDataLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-100 text-green-800';
      case 'offline': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const onlineCount = mergedSensors.filter(s => s.status === 'online').length;
  const offlineCount = mergedSensors.filter(s => s.status === 'offline').length;
  // The counts mean something only when the sensor list loaded and either the
  // status service said something about the sensors in it, or the farm's own
  // readings are there to derive from. Otherwise the cards show a dash, never
  // a 0.
  const statusKnown = (statusSource === 'api' || statusSource === 'derived') && !sensorsError;
  const statusUnknownNote = sensorsError ? 'Status not available'
    : statusSource === 'loading' ? 'Checking status…'
    : statusSource === 'no-readings' ? 'No readings yet'
    : 'Status not available';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Elliance</h1>
        <div className="flex items-center space-x-4">
          {hasOrganizations && <OrganizationSelector />}
          {hasFarms && <FarmSelector />}
        </div>
      </div>

      {/* Tab Navigation - Based on 3 API Endpoints */}
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { id: 'monitor', name: 'Greenhouse Monitor', icon: ChartBarIcon },
          { id: 'list', name: 'List Sensors', icon: ListBulletIcon },
          { id: 'status', name: 'Sensor Status', icon: SignalIcon },
          { id: 'data', name: 'Sensor Data', icon: ChartBarSquareIcon },
          { id: 'logs', name: 'API Logs', icon: DocumentTextIcon },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(tab.id)}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
              selectedTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`rounded-lg p-4 ${feedback.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center">
            {feedback.type === 'success' ? <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" /> : <XCircleIcon className="h-5 w-5 text-red-500 mr-3" />}
            <p className={`text-sm font-medium ${feedback.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{feedback.message}</p>
          </div>
        </div>
      )}

      {/* The sensor list failed to load — not the same as a farm with no sensors */}
      {sensorsError && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" role="alert">
          <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">
            <strong>Could not load the sensor list</strong> ({sensorsError}). This does not mean the
            farm has no sensors. Use Refresh on the Greenhouse Monitor tab to try again.
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Total Sensors</div>
            <ChartBarIcon className="h-5 w-5 text-blue-500" />
          </div>
          {sensorsError ? (
            <>
              <div className="text-2xl font-bold text-gray-400">—</div>
              <div className="text-xs text-gray-500">Sensor list not loaded</div>
            </>
          ) : (
            <div className="text-2xl font-bold text-gray-900">{mergedSensors.length}</div>
          )}
        </div>
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Online</div>
            <SignalIcon className="h-5 w-5 text-green-500" />
          </div>
          {statusKnown ? (
            <>
              <div className="text-2xl font-bold text-green-600">{onlineCount}</div>
              {statusSource === 'derived' && (
                <div className="text-xs text-gray-500">reporting in the last {ONLINE_WINDOW_MINUTES} min</div>
              )}
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-gray-400">—</div>
              <div className="text-xs text-gray-500">{statusUnknownNote}</div>
            </>
          )}
        </div>
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Offline</div>
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          </div>
          {statusKnown ? (
            <>
              <div className="text-2xl font-bold text-red-600">{offlineCount}</div>
              {statusSource === 'derived' && (
                <div className="text-xs text-gray-500">no reading in the last {ONLINE_WINDOW_MINUTES} min</div>
              )}
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-gray-400">—</div>
              <div className="text-xs text-gray-500">{statusUnknownNote}</div>
            </>
          )}
        </div>
      </div>

      {/* Greenhouse Monitor — registry and readings both live from the middleware */}
      {selectedTab === 'monitor' && (
        <GreenhouseMonitor
          registry={filteredSensors}
          registryError={sensorsError}
          readings={readings}
          source={readingsSource}
          loading={readingsLoading}
          issue={readingsIssue}
          meta={readingsMeta}
          onRefresh={() => {
            if (refreshReadingsRef.current) refreshReadingsRef.current();
            fetchSensors(selectedFarmId); // the sensor list (and status) too, so a failed list can be retried
          }}
          canPickFarm={hasFarms}
          farmLabel={selectedFarmId === 'all'
            ? 'All farms'
            : (farms.find(f => String(f.id) === String(selectedFarmId))?.farmName || `Farm ${selectedFarmId}`)}
        />
      )}

      {/* List Sensors Tab - API 1 */}
      {selectedTab === 'list' && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sensor Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading sensors from API...
                      </div>
                    </td>
                  </tr>
                ) : sensorsError ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-red-700" role="alert">
                      <p className="text-sm"><strong>Could not load the sensor list</strong> ({sensorsError}).</p>
                    </td>
                  </tr>
                ) : filteredSensors.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      <SignalIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-sm">No sensors found for this farm.</p>
                    </td>
                  </tr>
                ) : (
                  filteredSensors.map((sensor) => (
                    <tr key={sensor.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sensor.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <SignalIcon className="h-5 w-5 text-blue-500 mr-3" />
                          <div>
                            {/* Middleware returns sensorId + note (not the doc's `name`) */}
                            <div className="text-sm font-medium text-gray-900">{sensor.name || sensor.sensorId || `Sensor ${sensor.id}`}</div>
                            {sensor.note && <div className="text-xs text-gray-500">{sensor.note}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sensor.farmId}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sensor.vendorId}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => { setSelectedSensor(sensor); setSensorData(null); setSelectedTab('data'); }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Sensor Data"
                        >
                          <ChartBarSquareIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sensor Status Tab - API 2 */}
      {selectedTab === 'status' && (
        <div className="card overflow-hidden">
          {statusSource !== 'loading' && statusSource !== 'api' && !sensorsError && filteredSensors.length > 0 && (
            <p className="text-sm text-gray-600 mb-4">
              {statusState === 'unavailable'
                ? 'The sensor status service is not answering'
                : 'The sensor status service has no status for these sensors'}
              {statusSource === 'derived' ? (
                <>, so online / offline is derived from this farm's own readings
                  {readingsMeta?.loadedAt ? ` (loaded at ${formatClock(readingsMeta.loadedAt, now)})` : ''}: a sensor
                  is online when one of its measurements reported in the last {ONLINE_WINDOW_MINUTES} minutes.
                  {unmatchedCount > 0 && ` ${unmatchedCount} sensor${unmatchedCount > 1 ? 's' : ''} could not be matched to any measurement and stay${unmatchedCount > 1 ? '' : 's'} unknown.`}</>
              ) : (
                <>, and {statusSource === 'no-readings'
                  ? 'no readings have been published for this farm yet'
                  : readingsSource === 'all-farms'
                    ? 'no readings are loaded while All farms is selected'
                    : 'the readings could not be loaded'}, so every sensor is
                  shown as "unknown". This is not a fault at the farm.</>
              )}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sensor Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm ID</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
                ) : sensorsError ? (
                  <tr><td colSpan="4" className="px-6 py-8 text-center text-red-700" role="alert"><strong>Could not load the sensor list</strong> ({sensorsError}).</td></tr>
                ) : filteredSensors.length === 0 ? (
                  <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">No sensors found.</td></tr>
                ) : (
                  filteredSensors.map((sensor) => (
                    <tr key={sensor.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sensor.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <SignalIcon className={`h-5 w-5 mr-3 ${sensor.status === 'online' ? 'text-green-500' : sensor.status === 'offline' ? 'text-red-400' : 'text-gray-400'}`} />
                          <div>
                            {/* Middleware returns sensorId + note (not the doc's `name`) */}
                            <div className="text-sm font-medium text-gray-900">{sensor.name || sensor.sensorId || `Sensor ${sensor.id}`}</div>
                            {sensor.note && <div className="text-xs text-gray-500">{sensor.note}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(sensor.status)}`}>
                          {sensor.status || 'unknown'}
                        </span>
                        {statusSource === 'derived' && (
                          <div className="text-xs text-gray-500 mt-1">
                            {sensor.status === 'unknown'
                              ? 'no matching measurement'
                              : sensor.lastReadingAt
                                ? `last reading ${formatClock(sensor.lastReadingAt, now)} (${formatAge(sensor.lastReadingAt, now)})`
                                : 'no reading published for its measurements'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sensor.farmId}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sensor Data Tab - API 3 */}
      {selectedTab === 'data' && (
        <div className="space-y-4">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Sensor Data Query</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sensor</label>
                <select
                  className="input"
                  value={selectedSensor?.id || ''}
                  onChange={(e) => {
                    const s = mergedSensors.find(s => String(s.id) === e.target.value);
                    setSelectedSensor(s);
                    setSensorData(null); // the results on screen belong to the sensor picked before
                  }}
                >
                  <option value="">Select a sensor...</option>
                  {mergedSensors.map(s => (
                    <option key={s.id} value={s.id}>{s.name || s.sensorId || `Sensor ${s.id}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input type="datetime-local" className="input"
                  value={dateRange.startDate.replace(' ', 'T').slice(0, 16)}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value.replace('T', ' ') + ':00' }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input type="datetime-local" className="input"
                  value={dateRange.endDate.replace(' ', 'T').slice(0, 16)}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value.replace('T', ' ') + ':00' }))}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => selectedSensor && fetchSensorData(selectedSensor)}
                  disabled={!selectedSensor || sensorDataLoading}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {sensorDataLoading ? 'Loading...' : 'Fetch Data'}
                </button>
              </div>
            </div>
          </div>

          {/* Sensor Data Results */}
          {sensorData && (
            <div className="card">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Data for {selectedSensor?.name || selectedSensor?.sensorId || `Sensor ${selectedSensor?.id}`}
              </h4>
              {sensorDataNote?.issue && sensorDataNote.held && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4 text-sm text-amber-900" role="status">
                  <strong>{describeIssue(sensorDataNote.issue)}</strong>{' '}
                  {sensorDataNote.loadedAt
                    ? `Showing data loaded at ${formatClock(sensorDataNote.loadedAt, new Date())}.`
                    : 'Showing the last data loaded.'}
                </div>
              )}
              {sensorDataNote?.clipped && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-xs text-gray-600">
                  The selected range starts before {sensorDataNote.since.toLocaleString()}. Only the
                  last {SENSOR_DATA_WINDOW_DAYS} days of readings are kept in the browser, so nothing
                  older is listed.
                </div>
              )}
              {Object.keys(sensorData).length === 0 ? (
                sensorDataNote?.issue && !sensorDataNote.held ? (
                  // the request failed and nothing earlier is held: not "no data"
                  <p className="text-red-700 text-center py-8" role="alert">
                    <strong>Could not load sensor data.</strong> {describeIssue(sensorDataNote.issue)}
                  </p>
                ) : (
                  <p className="text-gray-500 text-center py-8">
                    {sensorDataNote?.noFarm
                      ? 'Choose a farm first: the sensor list does not say which farm this sensor belongs to, so no readings were requested.'
                      : `No data found for this ${sensorDataNote?.narrowed ? 'sensor' : 'farm'} in the selected date range.`}
                  </p>
                )
              ) : (
                <>
                  {sensorDataSource === 'reference' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
                      <strong>Elliance reference feed — not this farm.</strong> No readings are published
                      for this farm, so these come from Elliance's own tenant
                      (farmerId {ELLIANCE_REFERENCE_TENANT.farmerId}/farmId {ELLIANCE_REFERENCE_TENANT.farmId}).
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mb-4">
                    The middleware returns every metric published for the farm — it has no per-sensor
                    filter, so {sensorDataNote?.narrowed
                      ? 'the sensor you picked is applied here in the browser: only its own measurements are listed'
                      : 'every measurement in the feed is listed: this sensor is not one the page can match to its measurements'}.
                    The date range is applied here in the browser because the API ignores it. This tab
                    reads the same copy of the feed as the Greenhouse Monitor
                    {sensorDataNote?.loadedAt ? ` (loaded at ${formatClock(sensorDataNote.loadedAt, new Date())})` : ''};
                    it is downloaded at most once every {Math.round(SENSOR_DATA_CACHE_MS / 60000)} minutes
                    and only the last {SENSOR_DATA_WINDOW_DAYS} days are kept.
                  </p>
                  {Object.entries(sensorData).map(([metric, points]) => (
                    <div key={metric} className="mb-6">
                      <h5 className="text-sm font-semibold text-blue-800 bg-blue-50 px-3 py-2 rounded mb-2">
                        {metric} <span className="font-normal text-blue-600">· {points.length} reading{points.length > 1 ? 's' : ''}</span>
                      </h5>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {points.slice(-50).reverse().map((pt, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-gray-600">{pt.timestamp.toLocaleString()}</td>
                                <td className="px-4 py-2 font-medium text-gray-900">{pt.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {points.length > 50 && <p className="text-xs text-gray-500 mt-2 px-4">Showing the 50 most recent of {points.length} readings</p>}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* API Logs Tab */}
      {selectedTab === 'logs' && (
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">API Activity Log ({apiLogs.length} entries)</h3>
            <button
              onClick={() => {
                if (window.confirm('Clear all API logs?')) {
                  setApiLogs([]);
                  localStorage.removeItem('elliance_api_logs');
                }
              }}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Clear Logs
            </button>
          </div>
          {apiLogs.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No API activity recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiLogs.map((log, index) => (
                // ids are Date.now(), so two entries written in the same
                // millisecond share one - the position makes the key unique
                <div key={`${log.id}-${apiLogs.length - index}`} className={`border rounded-lg p-4 ${log.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {log.operation}
                      </span>
                      {log.status === 'success' ? <CheckCircleIcon className="h-5 w-5 text-green-600" /> : <XCircleIcon className="h-5 w-5 text-red-600" />}
                    </div>
                    <div className="flex items-center text-xs text-gray-500">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {new Date(log.timestamp).toLocaleString('en-MY')}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-semibold text-gray-700 mb-1"><span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">REQUEST</span></div>
                      <pre className="text-xs font-mono text-gray-800 bg-white rounded border p-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(log.request, null, 2)}</pre>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-700 mb-1"><span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded">RESPONSE</span></div>
                      <pre className="text-xs font-mono text-gray-800 bg-white rounded border p-2 overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">{JSON.stringify(log.response, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// A render error anywhere on the Elliance page shows a "Something went wrong"
// card with a Reload button instead of a blank screen. Only this page is wrapped.
const Elliance = () => (
  <PageErrorBoundary pageName="Elliance">
    <EllianceContent />
  </PageErrorBoundary>
);

export default Elliance;
